import React, { useEffect, useRef, useState } from 'react';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import { GET_ACCOUNTS } from '../constants/aryGraphqlSchemas';
import { useAryLazyQuery } from '../hooks/useAryGraphql';
import { translatePhraseItem } from '../utils/Utils';
import AryLazyAutoCompleteBase from './AryLazyAutoCompleteBase';

function AryAccountsLazyAutoComplete({
  pageCount = 8,
  accountType = GET_ACCOUNTS.accountType.ALL_CAN_TRANSACT,
  customerId,
  value,
  onChange,
  autoFillFirstOption,
  autoFillDraftSubmissionAccount,
  excludedAccountNumber,
  primitiveIncludedAccountNumbers = [],
  handleChangeTypeTitle,
  onBlur = () => {},
  initialAccountType,
  archivedAccounts,
  autoFocus,
  ...others
}) {
  // TODO: remove account type from
  const { t } = useFrameworkTranslation();
  const [options, setOptions] = useState([]);
  const selectedOptions = useRef([]);
  const disableAutoFillFirstOption = useRef(!autoFillFirstOption);
  const disableAutoFillDraftSubmissionAccount = useRef(
    !autoFillDraftSubmissionAccount
  );
  const [isMoreExist, setIsMoeExist] = useState(true);

  const [getAccounts, { loading }] = useAryLazyQuery({
    gqlConfig: GET_ACCOUNTS,
    items: `
       accounts {
         customerId
         accountNumber
         accountTitle {
           shouldTranslate
           text
         }
         fullName
         typeTitle
         customerType
       }
      totalCount
    `
  });

  function getOptionLabel(fullName, accountTitle) {
    const baseName = fullName || 'System Account';
    if (accountTitle.text) {
      return `${t(baseName)} - ${translatePhraseItem(t, accountTitle)}`;
    }
    return baseName;
  }

  async function loadOptions(pageIndex, searchText) {
    if (isMoreExist) {
      const { data: getAccountsData } = await getAccounts({
        accountType,
        searchText,
        customerId,
        showArchived: archivedAccounts || undefined,
        primitiveIncludedAccountNumbers: value
          ? primitiveIncludedAccountNumbers.indexOf(value) === -1
            ? primitiveIncludedAccountNumbers.concat(value)
            : primitiveIncludedAccountNumbers
          : undefined,
        excludedAccountNumbers: excludedAccountNumber
          ? [].concat(excludedAccountNumber)
          : [],
        pagination: { pageIndex, pageCount }
      });
      const finalTotalCount = pageCount * (pageIndex + 1);
      const totalCount = getAccountsData?.totalCount;
      if (pageIndex !== 0 && totalCount <= finalTotalCount) {
        setIsMoeExist(false);
      }
      const receivedOptions = getAccountsData?.accounts?.map(
        (customerAccount) => ({
          value: customerAccount.accountNumber,
          label: getOptionLabel(
            customerAccount.fullName,
            customerAccount.accountTitle
          ),
          typeTitle: customerAccount.typeTitle,
          customerId: customerAccount.customerId,
          customerType: customerAccount.customerType
        })
      );
      const newOptions = (pageIndex === 0 ? [] : options)
        .concat(receivedOptions)
        .concat(
          searchText ||
            accountType ===
              GET_ACCOUNTS.accountType.JUST_UNEXECUTED_DRAFT_ACCOUNT ||
            accountType === GET_ACCOUNTS.accountType.JUST_UNKNOWN_BANK_ACCOUNT
            ? []
            : selectedOptions.current.filter(
                (option) =>
                  receivedOptions
                    .map((receivedOption) => receivedOption.value)
                    .indexOf(option.value) === -1
              )
        );
      const newOptionsSet = [
        ...new Set(newOptions.map((option) => option?.value))
      ].map((optionValue) =>
        newOptions.find((obj) => obj?.value === optionValue)
      );

      setOptions(newOptionsSet);
      if (!disableAutoFillFirstOption.current) {
        if (!value) {
          const newOption =
            initialAccountType === 'CASH'
              ? newOptionsSet.find((opt) => opt.typeTitle === 'Cash')
              : newOptionsSet[0];
          onChange(newOption);
        }
        disableAutoFillFirstOption.current = true;
      }
      if (!disableAutoFillDraftSubmissionAccount.current) {
        const autoFilledOption = receivedOptions.filter(
          (item) => item.customerId === customerId
        );
        if (autoFilledOption?.length) {
          onChange(autoFilledOption[0]);
        } else {
          onChange(receivedOptions[0]);
        }
        disableAutoFillDraftSubmissionAccount.current = true;
      }
    }
  }

  useEffect(() => {
    if (
      (accountType === GET_ACCOUNTS.accountType.JUST_UNKNOWN_BANK_ACCOUNT ||
        accountType ===
          GET_ACCOUNTS.accountType.JUST_UNEXECUTED_DRAFT_ACCOUNT) &&
      options?.length === 1
    ) {
      selectedOptions.current = [].concat(options[0]);
      onChange(options[0]);
    }
  }, [accountType, options]);

  useEffect(() => {
    loadOptions(0, '');
  }, [
    JSON.stringify(primitiveIncludedAccountNumbers),
    JSON.stringify(excludedAccountNumber),
    accountType,
    customerId
  ]);

  useEffect(() => {
    if (!value) {
      selectedOptions.current = [];
    } else {
      const selectedObj = options?.find((obj) => obj?.value === value) || '';
      selectedOptions.current = selectedObj ? [].concat(selectedObj) : [];
      if (handleChangeTypeTitle) {
        handleChangeTypeTitle(selectedObj?.typeTitle);
      }
    }
  }, [value, options]);

  return (
    <AryLazyAutoCompleteBase
      options={options}
      autoFocus={autoFocus}
      loading={loading}
      // loading={loadingTest}
      isClearable
      value={options?.find((obj) => obj?.value === value) || ''}
      handleMoeExist={(isExist) => {
        setIsMoeExist(isExist);
      }}
      onChange={(selectedObj) => {
        if (selectedObj) {
          selectedOptions.current = [].concat(selectedObj);
        }
        onChange(selectedObj);
        // onBlur not call when option selected in list of options immediately
        if (onBlur) {
          setTimeout(() => {
            onBlur();
          }, 0);
        }
      }}
      loadOptions={loadOptions}
      {...others}
    />
  );
}

export default AryAccountsLazyAutoComplete;
